/**
 * Every user-visible string in Security Controls, verbatim from the design spec
 * (`design-specs/unit-05-security-controls-design.md`) and the write-API amendments in design.md.
 * Strings the spec does not give (zero-change lifecycle outcomes, the protected-path tooltip,
 * readable reason codes, Refresh while dirty) are the unit-07 plan's defaults (P6, P7, P8, P16).
 */

/** `path` when n is 1, else `paths`. */
export const pathWord = (n: number): string => (n === 1 ? 'path' : 'paths');
const changeWord = (n: number): string => (n === 1 ? 'change' : 'changes');

// E-01 / E-02
export const TOOL_TITLE = 'Security Controls';
export const pathCaption = (path: string): string => `Path: ${path}`;

// Tree pane (E-10 to E-16, tree states)
export const TREE_ARIA_LABEL = 'Security paths';
export const TREE_LOADING = 'Loading security paths...';
export const TREE_ERROR_TITLE = 'Security paths could not be loaded';
export const TRY_AGAIN = 'Try again';
export const FORBIDDEN_TITLE = 'Security Controls is not available';
export const FORBIDDEN_DESCRIPTION = 'You do not have permission to use Security Controls.';
export const TREE_FILTER_LABEL = 'Find a path';
export const TREE_FILTER_PLACEHOLDER = 'Find a path...';
export const TREE_FILTER_EMPTY = 'No paths match.';
/** The folder legacy listed every report-declared path under; it is not a security path itself. */
export const INSIGHT_REPORTING_LABEL = 'Insight Reporting';
/** The one folder every database-only (undeclared) path is listed under, last in the tree. */
export const STALE_REFERENCES_LABEL = 'Stale References';
export const STALE_REFERENCES_TOOLTIP =
  'Security entries saved for paths that nothing in Insight Cash declares: a legacy tool or report that is not migrated yet, ' +
  'one that was renamed or removed, or a report path waiting for a security catalogue refresh. Insight Cash does not check ' +
  'these paths today, so they do not change access here. They are kept so you can review them, and they may still matter to ' +
  'the legacy application or to a tool or report migrated later.';
/** The caption badge on a selected stale path. */
export const STALE_REFERENCE_BADGE = 'Stale reference';
export const UNKNOWN_TOOLTIP =
  'Stale reference: no tool, report or security tag declares this path. It is listed because security entries exist for it.';

// Tree commands: legacy's tree right-click menu, also reachable from visible controls
export const EXPAND_ALL = 'Expand all';
export const COLLAPSE_ALL = 'Collapse all';
export const RESET_TREE = 'Reset Tree';
export const SHOW_INACTIVE_ITEMS = 'Show inactive items';
export const SHOW_ONLY_INACTIVE_ITEMS = 'Show only inactive items';
export const HIDE_INACTIVE_ITEMS = 'Hide inactive items';
export const CLEAR_TAG_FILTERS = 'Clear all tag filters';
export const ONLY_INCLUDE_TAGS = 'Only include tag(s)';
export const EXCLUDE_TAGS = 'Exclude tag(s)';
export const NO_TAGS_AVAILABLE = 'No report tags are available';
export const TREE_MENU_LABEL = 'Tree commands';

// Editor states (E-29a to E-29g)
export const NO_PATH_TITLE = 'No path selected';
export const NO_PATH_DESCRIPTION = "Select an item in the tree to see and change each role's rights.";
export const RIGHTS_LOADING = 'Loading rights...';
export const LOAD_FAILED_TITLE = 'Could not load rights';
export const loadFailedMessage = (path: string): string =>
  `Rights for ${path} could not be loaded. Select it again to retry.`;
export const NO_ROLES_TITLE = 'No security roles to show';
export const NO_ROLES_DESCRIPTION = 'Roles appear here when they are active and apply to security.';

// Table (E-20 to E-28)
export const tableCaption = (path: string): string => `Rights for ${path}`;
export const COLUMN_HEADERS = [
  'Role Name',
  'Rights at This Level',
  'Applied Rights',
  'Decided By',
  'Modified On',
  'Modified By',
] as const;
/** The grid's group row, legacy's "Role Group Name:" caption (blank when the role has no group). */
export const roleGroupCaption = (group: string): string => `Role Group Name: ${group}`;
export const roleGroupToggleLabel = (group: string, collapsed: boolean): string =>
  `${collapsed ? 'Expand' : 'Collapse'} role group ${group === '' ? '(none)' : group}`;
export const levelTriggerLabel = (roleName: string): string => `Rights at this level for ${roleName}`;
export const CHANGED = 'Changed';
export const UPDATES_AFTER_SAVE = 'Updates after Save';
export const EMPTY_CELL = '—';
export const OVERRIDE_LABEL = 'Everyone Allow overrides this Deny';
export const overrideTooltip = (decidedAtPath: string): string =>
  `This role is denied at ${decidedAtPath}, but Everyone is allowed at the same level, and Allow wins. Members of this role can use this path.`;
export const SYSTEM_ADMIN_NOTE = 'System Administrators always have access and are not listed.';

// Banners (E-29, E-30)
export const INACTIVE_BANNER_TITLE = 'This path is inactivated';
export const inactiveBannerDescription = (inactivatedAt: string): string =>
  `Nobody can use this path while it is inactivated, whatever the levels below say. Inactivated at ${inactivatedAt}.`;
export const INACTIVE_BANNER_ADMIN_SELF = 'Use Actions, then Activate, to restore it.';
export const inactiveBannerAdminAncestor = (inactivatedAt: string): string => `Activate ${inactivatedAt} to restore it.`;
export const UNKNOWN_BANNER =
  'This is a stale reference: no tool, report or security tag declares this path, so Insight Cash does not check it. It is listed under Stale References because security entries exist for it.';

// Action bar (E-31 to E-36) and enablement tooltips
export const UNDO_LABEL = 'Undo Changes';
export const UNDO_TITLE = 'This will undo all the currently visible changes made.';
export const CLEAR_LABEL = 'Clear Settings';
export const CLEAR_TITLE = 'This will clear all the settings at this level.';
export const ACTIONS_LABEL = 'Actions';
export const INACTIVATE_ITEM = 'Inactivate...';
export const ACTIVATE_ITEM = 'Activate...';
export const SAVE_LABEL = 'Save';
export const SAVING_LABEL = 'Saving...';
export const SAVE_TITLE = 'This will save the changes for security at this level for all modified roles.';
export const SELECT_PATH_FIRST = 'Select a path first.';
export const SAVE_OR_UNDO_FIRST = 'Save or undo your changes first.';
export const ALREADY_INACTIVE = 'This path is already inactivated.';
export const PROTECTED_TOOLTIP = 'This path cannot be inactivated: it would lock everyone out of Security Controls.';
export const PATH_IS_ACTIVE = 'This path is active.';
export const inactivatedViaAncestor = (inactivatedAt: string): string =>
  `Inactivated at ${inactivatedAt}. Activate that path instead.`;

// Confirmations (E-40 to E-42)
export const INACTIVATE_PARAGRAPHS = [
  'Nobody, including System Administrators, can use this path or anything below it until it is activated again.',
  'A path below it that was explicitly re-activated stays active.',
] as const;
export const ACTIVATE_PARAGRAPHS = [
  "This path and everything below it become usable again, subject to each role's rights.",
  'Activating removes every inactivation record on these paths, including explicit re-activation records.',
] as const;
export const affectedCountText = (n: number): string =>
  n <= 1 ? 'This affects this path only.' : `This affects ${n} paths: this path and ${n - 1} below it.`;
export const LIFECYCLE_FORBIDDEN = 'Only System Administrators can inactivate or activate paths.';
export const lifecycleFailedPrefix = (verb: 'Inactivate' | 'Activate'): string => `${verb} failed. Nothing was changed.`;

// Guard (E-45)
export const LOSE_CHANGES_TITLE = 'Lose changes?';
export const LOSE_CHANGES_BODY = 'Changing security items will discard current changes, continue?';
export const CONTINUE = 'Continue';
export const CANCEL = 'Cancel';

// Save outcomes (E-50, E-51)
export const SAVED_TITLE = 'Security saved';
export const savedMessage = (n: number, path: string): string => `Saved ${n} ${changeWord(n)} to ${path}.`;
export const SAVE_FAILED_TITLE = 'Save failed';
export const SAVE_FORBIDDEN = 'You do not have permission to change security.';
export const saveInvalid = (readable: string): string => `The change was rejected: ${readable}`;
export const SAVE_CONFLICT =
  'Another change to this path was saved at the same time. Undo Changes to reload, then try again.';
export const SAVE_GENERIC = 'Data save error. Your changes are still on screen.';

// Refresh while dirty (P16)
export const REFRESH_BLOCKED_TITLE = 'Save or undo your changes first';
export const REFRESH_BLOCKED_MESSAGE = 'Refreshing would replace what you are editing.';

// Readable reason codes (P8)
export const REQUEST_FAILED = 'Request failed.';
export const READABLE_UNKNOWN_PATH = 'This path is not a known security path.';
export const READABLE_TOO_MANY_DESCENDANTS = 'This path has more than 2,000 paths below it. Choose a lower path.';
export const READABLE_SYSTEM_ADMIN_ROLE = 'The System Administrators role cannot be changed here.';
export const READABLE_EMPTY_ROLE_ID = 'A role was missing its identifier.';
export const readableNotActiveRole = (roleName: string): string =>
  `${roleName} is no longer an active security role. Undo Changes to reload.`;
export const readableDuplicateRole = (roleName: string): string => `${roleName} was listed twice.`;

// ── Tree presentation (unit-11 E-300 to E-332) ───────────────────────────────

export const pathLine = (path: string): string => `Path: ${path}`;
export const UNAVAILABLE_LABEL = 'Unavailable';
export const unavailableRootTooltip = (types: readonly string[]): string =>
  `Some Data Security values could not be loaded from the report service: ${types.join(', ')}. Those values are not listed right now. The rest of the tree is not affected.`;
export const unavailableFamilyTooltip = (securityType: string): string =>
  `The ${securityType} values could not be loaded from the report service, so they are not listed right now.`;
export const UNKNOWN_UNAVAILABLE_TEXT =
  'The report service did not return this value, so it cannot be confirmed right now. It is listed because security entries exist for it.';
export const DATA_SECURITY_BANNER_TITLE = 'Data Security values are unavailable';
export const dataSecurityBannerDescription = (types: readonly string[]): string =>
  `The report service did not return the ${types.join(', ')} values, so they cannot be listed or granted right now. Rights on this path can still be viewed and changed.`;
export const BLANK_VALUE_LABEL = '(Not Selected)';
export const dataSecurityLeafLine = (origin: string, label: string): string =>
  label === BLANK_VALUE_LABEL
    ? `Controls which roles can see ${origin} data with no ${origin} recorded.`
    : `Controls which roles can see ${origin} data for "${label}" in reports.`;
export const UI_ONLY_LINE =
  'This permission is checked by the browser only. It controls what screens show; it does not protect data on the server.';
export const CATALOGUE_NONE_ADMIN = 'Report paths are not listed yet. Use Refresh Security in the user menu to load them.';
export const CATALOGUE_NONE_OTHER = 'Report paths are not listed yet. Ask a System Administrator to refresh security.';
export const CATALOGUE_TRIGGER_LABEL = {
  'reporting-deploy': 'report deploy',
  'backend-deploy': 'application deploy',
  manual: 'manual refresh',
} as const;
export const catalogueRefreshedLine = (refreshedAt: string, trigger: string): string =>
  `Report paths last refreshed ${refreshedAt} (${trigger}).`;

// ── Refresh Security (E-100 to E-112) ────────────────────────────────────────

export const REFRESH_SECURITY_LABEL = 'Refresh Security';
export const REFRESHING_SECURITY_LABEL = 'Refreshing security...';
export const REFRESH_PROGRESS_TITLE = 'Refreshing security...';
export const REFRESH_PROGRESS_MESSAGE = 'Reading report security from the report service. This usually takes under 30 seconds.';
export const REFRESH_SUCCESS_TITLE = 'Security refreshed';

const reportWord = (n: number): string => (n === 1 ? 'report' : 'reports');
const tagWord = (n: number): string => (n === 1 ? 'tag' : 'tags');
const typeWord = (n: number): string => (n === 1 ? 'type' : 'types');
const entryWord = (n: number): string => (n === 1 ? 'entry' : 'entries');
const roleWord = (n: number): string => (n === 1 ? 'role' : 'roles');
const recordWord = (n: number): string => (n === 1 ? 'record' : 'records');

export interface RefreshSummaryInput {
  version: number;
  reports: number;
  tags: number;
  families: number;
  changedCount: number;
  pendingMoves: number;
}

/** E-105: up to three sentences joined by one space. */
export function refreshSuccessMessage({ version, reports, tags, families, changedCount, pendingMoves }: RefreshSummaryInput): string {
  const sentences = [
    `Checked ${reports} ${reportWord(reports)}, ${tags} security ${tagWord(tags)} and ${families} data security ${typeWord(families)}.`,
  ];
  if (version === 1) {
    // A first refresh has no earlier catalogue to compare with, so it cannot detect changes.
    sentences.push('Report paths are now listed in Security Controls.');
    return sentences.join(' ');
  }
  sentences.push(changedCount === 0 ? 'No report paths changed.' : `${changedCount} report ${pathWord(changedCount)} changed.`);
  if (pendingMoves > 0) {
    sentences.push(`${pendingMoves} path ${pendingMoves === 1 ? 'move is' : 'moves are'} waiting for review in Security Controls.`);
  }
  return sentences.join(' ');
}

export interface RefreshFailureCopy {
  type: 'error' | 'warning';
  title: string;
  message: string;
}

export const REFRESH_FORBIDDEN: RefreshFailureCopy = {
  type: 'error',
  title: 'Refresh failed',
  message: 'Only System Administrators can refresh security.',
};
export const REFRESH_CONFLICT: RefreshFailureCopy = {
  type: 'warning',
  title: 'Refresh already running',
  message: 'Another security refresh is in progress. Wait a minute, then try again.',
};
export const REFRESH_UNREACHABLE: RefreshFailureCopy = {
  type: 'error',
  title: 'Refresh failed',
  message:
    'The report service could not be reached. Nothing was changed, and Security Controls still shows the last refresh. Try again in a few minutes.',
};
export const REFRESH_SERVER_ERROR: RefreshFailureCopy = {
  type: 'error',
  title: 'Refresh failed',
  message: 'Security could not be refreshed. Try again. If it keeps failing, contact support.',
};
export const REFRESH_TIMEOUT: RefreshFailureCopy = {
  type: 'error',
  title: 'Refresh did not answer in time',
  message: 'The refresh may still finish. Open Security Controls in a minute to check, or try again.',
};

// ── Pending path moves: banner and review (E-200 to E-222) ───────────────────

export const MOVES_BANNER_TITLE = 'Report paths have changed';
export const movesBannerDescription = (n: number): string =>
  `${n} ${n === 1 ? 'report has' : 'reports have'} a new security path. ${n === 1 ? 'Its' : 'Their'} existing rights are still on the old path until you review ${n === 1 ? 'it' : 'them'}.`;
export const reviewMovesLabel = (n: number): string => `Review path moves (${n})`;
export const MOVES_CHECK_FAILED_TITLE = 'Path moves could not be checked';
export const MOVES_PANEL_TITLE = 'Pending path moves';
export const movesPanelSubtitle = (n: number): string => `${n} waiting for review`;
export const CLOSE = 'Close';
export const MOVES_LOADING = 'Loading path moves...';
export const MOVES_LOAD_FAILED_TITLE = 'Path moves could not be loaded';
export const MOVES_FORBIDDEN_TITLE = 'Path moves are not available';
export const MOVES_FORBIDDEN_DESCRIPTION = 'Only System Administrators can review path moves.';
export const MOVES_EMPTY_TITLE = 'No path moves waiting';
export const MOVES_EMPTY_DESCRIPTION = 'Every changed report path has been applied or dismissed.';
export const CONFLICT_LABEL = 'Conflict';
export const BEING_APPLIED_LABEL = 'Being applied';
export const BEING_APPLIED_PARAGRAPH = 'Another administrator is applying this move now. It will leave this list when they finish.';
export const BEING_APPLIED_TITLE = 'Being applied by another administrator.';
export const CHECK_AGAIN = 'Check again';
export const fromLine = (path: string): string => `From: ${path}`;
export const toLine = (path: string): string => `To: ${path}`;
export const detectedLine = (when: string): string => `Detected ${when}`;
export const moveRowSummary = (totalRows: number, roles: number, inactivationRows: number): string => {
  if (totalRows === 0) {
    return 'No security entries are on the old path, so there is nothing to copy. Dismiss this move if it is no longer needed.';
  }
  const tail =
    inactivationRows > 0
      ? `rights for ${roles} ${roleWord(roles)} and ${inactivationRows} inactivation ${recordWord(inactivationRows)}.`
      : `rights for ${roles} ${roleWord(roles)}.`;
  return `${totalRows} security ${entryWord(totalRows)} will be copied: ${tail}`;
};
export const ROLES_CAPTION = 'Roles with rights on the old path';
export const ROLE_COLUMN = 'Role';
export const ENTRIES_COLUMN = 'Entries';
export const PATHS_THAT_MOVE = 'Paths copied';
export const movePathLabel = (from: string, to: string): string => `${from} to ${to}`;
export const showAllLabel = (noun: 'paths' | 'rules', n: number): string => (noun === 'paths' ? `Show all ${n} paths` : `Show all ${n} rules`);
export const SHOW_FEWER = 'Show fewer';
export const ANCESTOR_RULES_LABEL = "Rules already on the new path's parent folders";
export const NO_ANCESTOR_RULES = "No Allow, Deny or inactivation is set on the new path's parent folders.";
export const ancestorRuleLine = (rule: { decision: string; roleName: string; path: string }): string => {
  switch (rule.decision) {
    case 'allow':
      return `Allow for ${rule.roleName} at ${rule.path}`;
    case 'deny':
      return `Deny for ${rule.roleName} at ${rule.path}`;
    case 'inactivated':
      return `Inactivated at ${rule.path}`;
    case 'reactivated':
      return `Reactivated at ${rule.path}`;
    default:
      return `${rule.decision} at ${rule.path}`;
  }
};
export const CONFLICT_TITLE = 'This move clashes with existing rights';
export const CONFLICT_PART_1 = 'These roles already have different rights on the new path, so nothing can be copied:';
export const CONFLICT_PART_2 =
  'Open each listed path in Security Controls and decide which rights the role should keep. Then apply this move again, or dismiss it if the new path is already right.';
export const conflictLine = (roleName: string, path: string): string => `${roleName} on ${path}`;
export const DISMISS_MOVE_ITEM = 'Dismiss...';
export const APPLY_MOVE_ITEM = 'Apply...';
export const dismissMoveAria = (reportName: string): string => `Dismiss path move for ${reportName}`;
export const applyMoveAria = (reportName: string): string => `Apply path move for ${reportName}`;
export const RESOLVE_CLASHES_FIRST = 'Resolve the clashes listed above first.';
export const NOTHING_TO_MOVE_TITLE = 'Nothing to copy. Dismiss this move instead.';
/** The confirm's action waits while the list is fetched again (red-team finding 2). */
export const CHECKING_MOVE = 'Checking this move is still current...';

// ── Apply and dismiss confirms (E-230 to E-244) ──────────────────────────────

export const APPLY_MOVE_ACTION = 'Apply move';
export const APPLYING_BUSY = 'Applying...';
export const DISMISS_MOVE_ACTION = 'Dismiss move';
export const DISMISSING_BUSY = 'Dismissing...';
/**
 * SS1 (architect decision 2026-10-26): Apply COPIES. The legacy desktop keeps checking the report's
 * old path in the same database, so the old entries stay where they are and keep applying there.
 */
export const applyParagraph1 = (oldPath: string, newPath: string): string =>
  `Every security entry on ${oldPath} and below it is copied to ${newPath}. Role rights and inactivation records are copied together. The entries on ${oldPath} stay as they are, so the desktop application keeps its access rules.`;
export const applyParagraph2 = (totalRows: number, roles: number): string =>
  `${totalRows} ${entryWord(totalRows)} for ${roles} ${roleWord(roles)} will be copied.`;
/** Design.md (unit-10 planning): grants follow the report; access is decided by the new location. */
export const APPLY_PARAGRAPH_3 =
  'In Insight Cash, access is then decided by the new location, so it can differ from today if the new path\'s parent folders have their own rules. Rights already on the new path are kept. The old path is listed under Stale References afterwards. Each change is written to the security history.';
/**
 * E-235 paragraph 1. The spec's sentence ("which no report uses any more, so it will be listed as
 * Unknown", now "listed under Stale References") is kept for the case it describes. It is false in two others, so those get
 * their own sentence rather than a promise the tree will not keep: the old path is still a known
 * path (another report, a registry entry or a tag uses it, as with OLD_PATH_IN_USE), or no entries
 * are stored on it at all.
 */
export const dismissParagraph1 = (oldPath: string, oldPathInUse: boolean, totalRows: number): string => {
  if (totalRows === 0) return `Nothing moves. No security entries are on ${oldPath}, so nothing is left behind.`;
  if (oldPathInUse) return `Nothing moves. The entries stay on ${oldPath}, which is still in use, so they keep applying there.`;
  return `Nothing moves. The entries stay on ${oldPath}, which no report uses any more, so it will be listed under Stale References.`;
};
export const dismissParagraph2 = (newPath: string): string =>
  `The report now checks ${newPath}. A role that had the report only through the old path loses it until you grant it on the new path.`;
export const DISMISS_PARAGRAPH_3 = "This cannot be undone here. The move is listed again only if the report's path changes again.";

export const MOVE_APPLIED_TITLE = 'Path move applied';
export const MOVE_RECORD_NOT_UPDATED =
  'The rights were copied, but the move\'s record could not be marked applied. Refresh the list to check.';
export const moveAppliedMessage = (
  copiedRows: number,
  pathCount: number,
  oldPath: string,
  newPath: string,
  archivedRows: number,
  recordUpdated: boolean,
): string => {
  const base = `Copied ${copiedRows} security ${entryWord(copiedRows)} on ${pathCount} ${pathWord(pathCount)} to ${newPath}. The entries on ${oldPath} are unchanged. ${archivedRows} history ${recordWord(archivedRows)} written.`;
  return recordUpdated ? base : `${base} ${MOVE_RECORD_NOT_UPDATED}`;
};
export const MOVE_DISMISSED_TITLE = 'Path move dismissed';
export const moveDismissedMessage = (reportName: string, oldPath: string): string =>
  `${reportName} keeps its new path. The old entries stay on ${oldPath}.`;
export const MOVES_FORBIDDEN_INLINE = 'Only System Administrators can apply or dismiss path moves.';
export const ALREADY_HANDLED_TITLE = 'Already handled';
export const ALREADY_HANDLED_MESSAGE = 'Someone else already applied or dismissed this move. The list has been refreshed.';
export const MOVE_GONE_TITLE = 'Move no longer listed';
export const MOVE_GONE_MESSAGE = 'This move is no longer recorded. The list has been refreshed.';
export const MOVE_CONFLICT_TITLE = 'Path move not applied';
export const moveConflictMessage = (reportName: string, newPath: string): string =>
  `${reportName} clashes with rights already on ${newPath}. Nothing was copied.`;
export const MOVE_STALE_TITLE = 'Path move out of date';
export const MOVE_STALE_MESSAGE =
  "The report's path changed since this move was detected. Use Refresh Security, then review the move again. Nothing was copied.";
export const MOVE_IN_PROGRESS_TITLE = 'Move in progress';
export const MOVE_IN_PROGRESS_MESSAGE = 'Another administrator is applying this move right now. Check the list again in a moment.';
export const NOTHING_TO_MOVE_TOAST_TITLE = 'Nothing to copy';
export const nothingToMoveMessage = (oldPath: string): string =>
  `No rights are stored at ${oldPath}, so there was nothing to copy. The move is still listed: dismiss it if it is no longer needed.`;
export const NOT_MOVABLE_TITLE = 'This move cannot be applied';
const NOT_MOVABLE_ACTION = 'It cannot be moved here. Dismiss the move, or change the path by hand.';
export const NOT_MOVABLE_UNKNOWN_RULE = `This move names a path that cannot be moved. Dismiss the move, or change the path by hand.`;
const NOT_MOVABLE_REASON: Readonly<Record<string, string>> = {
  PATH_SHAPE: 'is not a valid security path.',
  DATA_SECURITY: 'is under Data Security, which is managed separately.',
  NOVA_PERMISSION: 'is a path that an application tool or screen declares.',
  CONTAINS_NOVA_PERMISSION: 'is a folder that holds application tools or screens.',
  BELOW_NOVA_PERMISSION: 'is inside an application tool, so its rights belong to that tool.',
  CONTAINS_REPORT: 'is a folder that holds other reports.',
  OLD_PATH_IN_USE: 'is still used by another report.',
};
export const notMovableMessage = (which: 'oldPath' | 'newPath', rule: string | undefined): string => {
  const reason = rule === undefined ? undefined : NOT_MOVABLE_REASON[rule];
  if (reason === undefined) return NOT_MOVABLE_UNKNOWN_RULE;
  return `${which === 'oldPath' ? 'The old path' : 'The new path'} ${reason} ${NOT_MOVABLE_ACTION}`;
};
export const moveFailedInline = (verb: 'Apply' | 'Dismiss', serverMessage?: string): string =>
  serverMessage === undefined ? `${verb} failed. Nothing was changed.` : `${verb} failed. Nothing was changed. ${serverMessage}`;
